import type { KnockFailure, PublicDoorFailureCode } from "../../lib/api";

/**
 * What a knock says when it cannot happen — Nob's side of a refusal, in words.
 *
 * Its own module because the sentences outlived the screen that first held
 * them. They were written for the door's own intake; the intake is gone (ruling
 * R-0827-1 — a signed-in visitor knocks in the console), and the console's
 * compose pane says the same things for the same reasons. Copy that two screens
 * would have to keep in step is copy that belongs to neither.
 */

/** The owner, standing at their own plate. Not an intake — the server refuses it. */
export const OWN_DOOR_ASIDE = "This is the plate people see.";
export const OWN_DOOR_ACTION = "Your door";
export const OWN_DOOR_NOTE = "Nob answers it — you do not knock at it.";

/**
 * Paid features are suspended while a dispute is open (ToU 2.2 §8.9). Said
 * once, without a figure and without an action: there is nothing this person
 * can buy their way past, which is the whole point of the suspension.
 */
export const PAYMENT_SUSPENDED_COPY =
  "Paid features are suspended while a payment dispute is open on this account.";

/**
 * Nob's side of a refusal, in Nob's words (§5.3 codes). `PLAN_EXHAUSTED` has no
 * line: it is answered by the plan card, which carries the three roads out of
 * it rather than telling the sender off about money.
 *
 * `DOOR_SPEND_CAP` is not here and cannot come back: the per-door cap protected
 * against door-set prices, and there are none (ruling C3).
 */
export function messageFor(failure: KnockFailure): string | null {
  switch (failure.code) {
    case "PLAN_EXHAUSTED":
      return null;
    // The other money refusal, and a 403 rather than a 402 (§8.9). Nothing can
    // be bought while it stands, so the plan card would be a road that goes
    // nowhere and this is a sentence instead.
    case "PAYMENT_RISK_SUSPENDED":
      return PAYMENT_SUSPENDED_COPY;
    // No `KNOCK_ACTIVE` arm and no sentence for one. Knocking where a row
    // already exists is not a refusal any more — there is one conversation per
    // sender and door, and the server answers with it (§2).
    case "DOOR_CLOSED":
      return "This door is closed.";
    case "OWN_DOOR":
      // Normally unreachable — the owner gets the interstitial, not a composer —
      // but a stale session can race the `me` read, and the server's refusal
      // deserves its sentence rather than a shrug.
      return "This is your own door. Nob answers it for you.";
    case "RATE_LIMITED":
      return "That’s enough knocking for today.";
    case "VALIDATION_ERROR":
      return "Nob could not read that. Check what you entered.";
    case "UNAUTHENTICATED":
    case "UNKNOWN":
      return "Nob is not answering. Try that again.";
  }
}

/** Paused, gone, or never there — three different things, said as three (FR-3.1). */
export function doorMessageFor(code: PublicDoorFailureCode): string {
  switch (code) {
    case "DOOR_CLOSED":
      return "This door is closed.";
    case "DOOR_NOT_FOUND":
      return "There is no door at that handle.";
    case "DOOR_SUPPRESSED":
      return "That door is not available.";
    case "UNKNOWN":
      return "Nob is not answering. Try that again.";
  }
}
